/**
 * Sidebar navigation item for the CRM module (raptor-crm-fe).
 *
 * A single entry that navigates into the CRM micro-frontend. Once inside,
 * the CRM's own sidebar handles all internal navigation. A "Back to RapidWorks"
 * control in CrmRoutes allows the user to return to the host application.
 *
 * GATING (LLAMS-465): visibility is driven by the canonical useCanAccessCrm()
 * rule — LaunchDarkly's "crm.module.enabled" flag AND the JWT's CrmLicense
 * claim, both required. This is deliberately NOT expressed via the generic
 * `featureFlag`/useFilterNavItemsByFeatureFlags mechanism used elsewhere in the
 * sidebar, since that utility can only check a single flag and can't express
 * the flag+claim combination — doing so here would duplicate half the rule.
 * The same useCanAccessCrm() hook also gates the /crm/* route itself
 * (see CrmAccessGuard), so visibility and access can never drift apart.
 *
 * FEATURE FLAG: crm.module.enabled (FEATURE_FLAGS.CRM_MODULE_ENABLED)
 *   - Created in LaunchDarkly. Development environment: always true.
 *   - Production: controlled per organisation via targeting rules.
 *
 * HOW TO CONTROL PER CLIENT:
 *   In the LaunchDarkly console, open the flag "crm.module.enabled" → Targeting tab:
 *   1. Add a rule: "If organization key is <dbAlias> → serve true".
 *      The dbAlias comes from the JWT and uniquely identifies each customer DB.
 *   2. Keep the default rule as "false" so new clients are off by default.
 *   The identify() call with the real user context happens in AuthContext.tsx
 *   (syncLaunchDarklyIdentity) immediately after login.
 */

import { Business, BusinessTwoTone } from '@mui/icons-material';

import { NavItemType } from '@/common/presentation/components/layout/Sidebar/type';
import { useCanAccessCrm } from '@/crm/application/useCanAccessCrm';

export interface CrmNavItemsResult {
  item: NavItemType | null;
  isReady: boolean;
}

const CrmNavItems = (): CrmNavItemsResult => {
  const { allowed, isReady } = useCanAccessCrm();

  // disabled (not omitted) is required here: SidebarNavGroup's 'collapse' branch
  // already safely renders null for a disabled item, whereas an entry missing
  // from the returned array falls through to its default case and renders a
  // visible "Menu Items Error" — verified against SidebarNavItemGroup.tsx.
  const navItems: NavItemType = {
    id: 'crm',
    title: 'CRM',
    type: 'collapse',
    icon: BusinessTwoTone,
    iconSelected: Business,
    showChevron: false,
    url: '/crm/dashboard',
    disabled: !allowed
  };

  return { item: navItems, isReady };
};

export default CrmNavItems;
